import React, { useEffect, useState } from 'react';

import { type MotionValue } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { ReactComponent as StarIcon } from '@pkg/assets/icons/v2/star-icon.svg';
import { ReactComponent as StarIconPointy } from '@pkg/assets/icons/v2/star-outline.svg';
import { useHandleWishlist } from '@pkg/components/Wishlist/hooks';
import { useAppDispatch, useAuthentication, useItemInWishlists } from '@pkg/hooks';

import { type CollectionChainType } from '@app/client';
import { wishlistActions } from '@app/store/slices';

import { Button, CountText, IconButtonWrapper, StarButton } from './styles';

export interface AddToWishlistButtonProps {
    isLoading?: boolean;
    isPrimary?: boolean;
    added?: boolean;
    contract?: string;
    tokenId?: string;
    slug?: string;
    iconButton?: boolean;
    image?: string;
    name?: string;
    chain: CollectionChainType;
    style?: {
        height: MotionValue<number>;
    };
    wishlistSlug?: string;
    wishlistedCount?: string;
    onClicked?: () => void;
    onError?: () => void;
}

export const AddToWishlistButton = ({
    added,
    isLoading,
    isPrimary,
    wishlistedCount,
    contract,
    tokenId,
    slug,
    style,
    iconButton,
    onClicked,
    chain,
    onError,
    image,
    name,
}: AddToWishlistButtonProps) => {
    function onSuccess() {
        setIsAdded(true);

        dispatch(
            wishlistActions.setWishListModalData({
                name,
                image,
                contract_address: contract,
                token_id: tokenId,
                slug,
            }),
        );
    }

    const {
        handleRemoveFromWishlist,
        handleAddFlow,
        isAddedItem,
        isLoading: isLoadingItems,
    } = useHandleWishlist({
        contract,
        image,
        name,
        onClicked,
        onError,
        onSuccess,
        chain,
        collectionSlug: slug,
        tokenId,
    });

    const { protectedAction } = useAuthentication();

    const [isAdded, setIsAdded] = useState(false);

    const { isFetching } = useItemInWishlists({
        contract,
        token_id: tokenId,
        slug,
        disabled: added,
        chain,
    });

    const loading = isFetching || isLoading || isLoadingItems;

    useEffect(() => {
        setIsAdded(isAddedItem);
    }, [isAddedItem]);

    const { t: translate } = useTranslation(['nft_details']);

    const dispatch = useAppDispatch();

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        protectedAction(() => {
            if (loading) return;

            onClicked && onClicked();

            if (isAdded) {
                setIsAdded(false);
                handleRemoveFromWishlist();
            } else {
                handleAddFlow();
            }
        });
    };

    // TODO: this can be deleted after the new CardTile component is released
    if (iconButton) {
        return (
            <IconButtonWrapper>
                <StarButton
                    $isLoading={loading}
                    $inWishlist={isAdded}
                    onClick={handleClick}
                    title={
                        isAdded
                            ? translate(`detail_view_remove_from_wishlist`)
                            : translate('detail_view_add_to_wishlist')
                    }
                >
                    <StarIconPointy />
                    {wishlistedCount && <CountText>{wishlistedCount}</CountText>}
                </StarButton>
            </IconButtonWrapper>
        );
    }

    return (
        <Button style={style} $loading={loading} $isAdded={isAdded} $isPrimary={isPrimary} onClick={handleClick}>
            <>
                <StarIcon />
                {isAdded ? translate(`detail_view_added_to_wishlist`) : translate('detail_view_add_to_wishlist')}
            </>
        </Button>
    );
};
